import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { downloadCsv } from "@/lib/exportCsv";
import { formatDateTime } from "@/lib/dateTime";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { StatusPill, type Tone } from "@/components/patterns";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CAMPAIGN_STATUS, ProgressBar } from "./CampaignsTab";

type Detail = {
  id: string; name: string; template_name: string; status: string; created_by: string | null; created_on: string; scheduled_at: string | null;
  first_send_at: string | null; last_send_at: string | null; daily_cap: number; last_error: string | null; planned: number; skipped: number; progress: number;
  window: { start_min: number; end_min: number }; skip_recent_days: number;
  counts: { queued: number; sent: number; delivered: number; read: number; failed: number; cancelled: number; replied: number };
  skipped_by_reason: Array<{ reason: string; count: number }>;
  follow_ups: number; task_on_no_reply: boolean; end_wait_minutes: number;
  steps: Array<{ step: number; template: string; delay_minutes: number; waiting: number; sent: number; delivered: number; read: number; replied: number; failed: number; cancelled: number; skipped: number; skipped_reasons: Array<{ reason: string; count: number }> }>;
};
type Recipient = { lead_id: string; step: number; name: string; company: string; wa_id: string | null; outcome: string; reason: string | null; planned_at: string | null; sent_at: string | null; replied: boolean };

const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const OUTCOME: Record<string, { label: string; tone: Tone }> = {
  PLANNED: { label: "Waiting", tone: "neutral" }, QUEUED: { label: "Waiting", tone: "neutral" }, RETRYING: { label: "Retrying", tone: "warning" },
  SENT: { label: "Sent", tone: "info" }, DELIVERED: { label: "Delivered", tone: "success" }, READ: { label: "Read", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" }, CANCELLED: { label: "Cancelled", tone: "warning" }, SKIPPED: { label: "Skipped", tone: "neutral" },
};
const FILTERS = [["", "Everyone"], ["queued", "Waiting"], ["sent", "Sent"], ["delivered", "Delivered"], ["read", "Read"], ["replied", "Replied"], ["failed", "Failed"], ["skipped", "Skipped"], ["cancelled", "Cancelled"]] as const;
const PAGE = 50;

/** One campaign: how it is going, why leads were skipped, and what happened to each lead */
export function CampaignDetailPage() {
  const { id } = useParams();
  const { success, error } = useToast();
  const [c, setC] = useState<Detail | null>(null);
  const [missing, setMissing] = useState(false);
  const [state, setState] = useState("");
  const [step, setStep] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<{ total: number; rows: Recipient[] } | null>(null);

  const load = useCallback(async () => {
    try {
      setC(unwrapObject<Detail>(await api.get(`/api/WhatsAppCampaign/ById/${id}`)));
      setRows(unwrapObject<{ total: number; rows: Recipient[] }>(await api.get(`/api/WhatsAppCampaign/Recipients/${id}?state=${state}&step=${step}&page=${page}&page_size=${PAGE}`)));
    } catch {
      setMissing(true);
    }
  }, [id, state, step, page]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!c || !["BUILDING", "SCHEDULED", "RUNNING"].includes(c.status)) return;
    const t = setInterval(() => void load(), 10000);
    return () => clearInterval(t);
  }, [c, load]);

  const cancel = async () => {
    if (!window.confirm("Stop this campaign? Messages not yet sent are cancelled. Messages already sent stay.")) return;
    try {
      const r = unwrapObject<{ cancelled: number }>(await api.post(`/api/WhatsAppCampaign/Cancel/${id}`));
      success("Campaign stopped", `${r.cancelled} message${r.cancelled === 1 ? "" : "s"} cancelled`);
      void load();
    } catch (e: any) {
      error("Couldn't stop the campaign", e?.message || "Please try again");
    }
  };

  const exportCsv = async () => {
    const all = unwrapObject<{ rows: Recipient[] }>(await api.get(`/api/WhatsAppCampaign/Recipients/${id}?state=${state}&step=${step}&page=1&page_size=5000`));
    downloadCsv(`${(c?.name || "campaign").replace(/[^\w-]+/g, "-")}.csv`, [
      ["Message", (r: Recipient) => (r.step === 0 ? "First message" : `Follow-up ${r.step}`)], ["Lead", (r: Recipient) => r.name], ["Company", (r: Recipient) => r.company], ["WhatsApp number", (r: Recipient) => r.wa_id], ["Result", (r: Recipient) => OUTCOME[r.outcome]?.label || r.outcome],
      ["Replied", (r: Recipient) => (r.replied ? "Yes" : "")], ["Sent at", (r: Recipient) => (r.sent_at ? formatDateTime(r.sent_at) : "")], ["Note", (r: Recipient) => r.reason || ""],
    ], all.rows);
  };

  if (missing) return <div className="p-6 text-sm text-muted-foreground">This campaign isn't available. <Link to="/whatsapp" className="text-primary hover:underline">Back to WhatsApp</Link></div>;
  if (!c) return <div className="flex justify-center py-16"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>;
  const st = CAMPAIGN_STATUS[c.status] || { label: c.status, tone: "neutral" as Tone };
  const stat = (label: string, n: number) => (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div><div className="text-xl font-semibold tabular-nums">{n}</div></div>
  );
  const open = ["BUILDING", "SCHEDULED", "RUNNING"].includes(c.status);
  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/whatsapp?tab=campaigns" className="mb-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="size-3.5" />WhatsApp</Link>
          <h1 className="flex flex-wrap items-center gap-2 text-[19px] font-semibold tracking-tight">{c.name}<StatusPill tone={st.tone}>{st.label}</StatusPill></h1>
          <p className="text-xs text-muted-foreground">Template {c.template_name} · by {c.created_by || "—"} · up to {c.daily_cap} a day, {hhmm(c.window.start_min)}–{hhmm(c.window.end_min)}{c.first_send_at ? ` · ${formatDateTime(c.first_send_at)} to ${formatDateTime(c.last_send_at || c.first_send_at)}` : ""}</p>
        </div>
        {open && <Button variant="outline" size="sm" onClick={cancel}>Stop campaign</Button>}
      </div>
      {c.last_error && <p className="text-sm text-destructive" role="alert">{c.last_error}</p>}
      <div className="flex items-center gap-3"><ProgressBar value={c.progress} /><span className="text-xs text-muted-foreground">{c.counts.sent} of {c.planned} sent</span></div>
      {c.status === "RUNNING" && c.counts.queued === 0 && (c.follow_ups > 0 || c.task_on_no_reply) && (
        <p className="text-xs text-muted-foreground">Waiting for follow-ups: the next message goes only to leads who have not replied{c.task_on_no_reply ? `, and a call task is created for the owner of anyone still silent ${Math.round((c.end_wait_minutes / 1440) * 10) / 10} day${c.end_wait_minutes === 1440 ? "" : "s"} after the last message` : ""}.</p>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {stat("Waiting", c.counts.queued)}{stat("Sent", c.counts.sent)}{stat("Delivered", c.counts.delivered)}{stat("Read", c.counts.read)}{stat("Replied", c.counts.replied)}{stat("Failed", c.counts.failed)}{stat("Skipped", c.skipped)}
      </div>
      {c.steps.length > 1 && (
        <div className="rounded-lg border border-border bg-card p-3 text-[13px]">
          <div className="mb-2 font-semibold">The sequence</div>
          <ol className="space-y-2">
            {c.steps.map((st) => (
              <li key={st.step} className="grid gap-1 sm:grid-cols-[220px_1fr] sm:items-center">
                <div>
                  <div className="font-medium">{st.step === 0 ? "First message" : `Follow-up ${st.step}`} · {st.template}</div>
                  {st.step > 0 && <div className="text-xs text-muted-foreground">{Math.round((st.delay_minutes / 1440) * 10) / 10} day{st.delay_minutes === 1440 ? "" : "s"} after the previous message, only if no reply</div>}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                  <span>Waiting <strong className="text-foreground">{st.waiting}</strong></span><span>Sent <strong className="text-foreground">{st.sent}</strong></span><span>Delivered <strong className="text-foreground">{st.delivered}</strong></span>
                  <span>Read <strong className="text-foreground">{st.read}</strong></span><span>Replied <strong className="text-foreground">{st.replied}</strong></span><span>Skipped <strong className="text-foreground">{st.skipped}</strong></span>
                  {st.skipped_reasons.map((r) => <span key={r.reason} className="basis-full text-xs">• {r.count} — {r.reason}</span>)}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {c.skipped_by_reason.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-3 text-[13px]">
          <div className="mb-1 font-semibold">Why leads were skipped</div>
          {c.skipped_by_reason.map((r) => <div key={r.reason} className="text-muted-foreground">{r.count} — {r.reason}</div>)}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setState(k); setPage(1); }} className={`rounded-full px-2.5 py-1 text-[12.5px] ${state === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {c.steps.length > 1 && (
          <div className="flex flex-wrap gap-1.5" aria-label="Message">
            {[["", "All messages"], ...c.steps.map((st) => [String(st.step), st.step === 0 ? "First message" : `Follow-up ${st.step}`])].map(([k, l]) => (
              <button key={k} type="button" onClick={() => { setStep(k as string); setPage(1); }} className={`rounded-full px-2.5 py-1 text-[12.5px] ${step === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
        )}
        <Button variant="outline" size="sm" onClick={exportCsv}><Download className="mr-1.5 size-4" />Download CSV</Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>Lead</TableHead>{c.steps.length > 1 && <TableHead>Message</TableHead>}<TableHead>Result</TableHead><TableHead>Planned</TableHead><TableHead>Sent</TableHead><TableHead>Note</TableHead></TableRow></TableHeader>
          <TableBody>
            {(rows?.rows || []).map((r) => {
              const o = OUTCOME[r.outcome] || { label: r.outcome, tone: "neutral" as Tone };
              return (
                <TableRow key={r.lead_id}>
                  <TableCell><Link to={`/leads/${r.lead_id}`} className="font-medium text-primary hover:underline">{r.name}</Link><div className="text-xs text-muted-foreground">{r.company}</div></TableCell>
                  {c.steps.length > 1 && <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{r.step === 0 ? "First message" : `Follow-up ${r.step}`}</TableCell>}
                  <TableCell><StatusPill tone={o.tone}>{o.label}</StatusPill>{r.replied && <StatusPill tone="gold" className="ml-1.5">Replied</StatusPill>}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{r.planned_at ? formatDateTime(r.planned_at) : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{r.sent_at ? formatDateTime(r.sent_at) : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.reason || ""}</TableCell>
                </TableRow>
              );
            })}
            {rows && rows.rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">Nobody here.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {rows && rows.total > PAGE && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{(page - 1) * PAGE + 1}–{Math.min(page * PAGE, rows.total)} of {rows.total}</span>
          <div className="flex gap-2"><Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button><Button size="sm" variant="outline" disabled={page * PAGE >= rows.total} onClick={() => setPage((p) => p + 1)}>Next</Button></div>
        </div>
      )}
    </div>
  );
}
